import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import * as C from "@/lib/constants";
import { PageHeader, Spinner, PriorityBadge } from "@/components/shared";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RuleFormDialog from "@/components/admin/RuleFormDialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const readCondition = (c) => {
  const field = C.RULE_FIELDS.find((f) => f.value === c.field);
  const fieldLabel = field?.label || c.field;
  const op = C.RULE_OPERATORS.find((o) => o.value === c.operator);
  const opLabel = op?.label || c.operator;
  let valLabel = "";
  if (op?.needsValue) {
    const vals = Array.isArray(c.value) ? c.value : [c.value];
    valLabel = vals.map((v) => (field ? C.labelFor(field.options, v) : v)).join(", ");
  }
  return `${fieldLabel} ${opLabel}${valLabel ? " " + valLabel : ""}`;
};

export default function AdminRules() {
  const [rules, setRules] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    const [{ data: r }, { data: p }] = await Promise.all([
      api.get("/admin/roadmap-rules"),
      api.get("/admin/programs"),
    ]);
    setRules(r); setPrograms(p); setLoading(false);
  };
  useEffect(() => { load(); }, []);
  if (loading) return <Spinner />;

  const toggle = async (r) => {
    try {
      await api.patch(`/admin/roadmap-rules/${r.id}`, { ...r, active: !r.active });
      setRules((rs) => rs.map((x) => x.id === r.id ? { ...x, active: !x.active } : x));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/roadmap-rules/${deleting.id}`);
      toast.success("Rule deleted."); setDeleting(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r) => { setEditing(r); setFormOpen(true); };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="Content" title="Roadmap Rule Manager" subtitle="Deterministic rules that decide which programs and actions appear on a family's roadmap." />
        <Button onClick={openNew} className="shrink-0" data-testid="admin-rule-new"><Plus size={16} className="mr-1" /> New rule</Button>
      </div>

      <div className="space-y-3">
        {rules.map((r) => (
          <div key={r.id} className="kid-card p-5 flex items-start justify-between gap-4" data-testid="admin-rule-row">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <PriorityBadge priority={r.priority} />
                <span className="text-xs text-kid-muted">{r.roadmap_section.replace(/_/g, " ")} · {r.recommendation_type}</span>
              </div>
              <h4 className="font-semibold text-kid-navy">{r.title}</h4>
              <p className="text-sm text-kid-muted mt-0.5">{r.display_text}</p>
              {r.conditions?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {r.conditions.map((c, i) => (
                    <span key={i} className="text-xs bg-kid-mist text-kid-navy/80 px-2 py-1 rounded-full" data-testid="rule-condition-chip">{readCondition(c)}</span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-kid-muted/70 italic mt-2">Applies to every family</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="flex flex-col items-center gap-1">
                <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid="admin-rule-toggle" />
                <span className="text-xs text-kid-muted">{r.active ? "Active" : "Off"}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(r)} className="text-kid-teal p-1.5 rounded-lg hover:bg-kid-mist" title="Edit" data-testid="admin-rule-edit"><Pencil size={16} /></button>
                <button onClick={() => setDeleting(r)} className="text-kid-coral p-1.5 rounded-lg hover:bg-kid-mist" title="Delete" data-testid="admin-rule-delete"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <RuleFormDialog open={formOpen} onOpenChange={setFormOpen} rule={editing} programs={programs} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="rule-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>“{deleting?.title}” will be removed. Existing roadmaps stay as-is until regenerated. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rule-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-kid-coral hover:bg-kid-coral/90" data-testid="rule-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
